import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

// Button-triggered dropdown that expands to a small table of relievers.
// Columns: Pitcher (name + rank if in probable-pitcher history), Hand,
// Avg Inn (avgEnteredInning), Days (since last appearance).
// The popover is rendered to document.body via React Portal so it can't be
// clipped or z-index-buried by sticky ancestors (the matchups-panel headers
// otherwise cover it). Position is `fixed` and tracked from the trigger's
// bounding rect, kept up to date on scroll and resize.
const BullpenDropdown = ({ bullpenList, selectedId, onSelect, mobile = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const updatePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left });
  }, []);

  // Position + close-on-outside-click / Escape / scroll / resize.
  useEffect(() => {
    if (!open) return;
    updatePos();
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, updatePos]);

  if (!Array.isArray(bullpenList) || bullpenList.length === 0) return null;
  const selected = bullpenList.find(p => String(p.id) === String(selectedId));
  const isActive = !!selected;
  const label = selected
    ? `${selected.rank ? `${selected.rank}. ` : ''}${selected.name}${selected.hand ? ` (${selected.hand})` : ''}`
    : `Bullpen (${bullpenList.length})`;

  const fmtAvgInn = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v.toFixed(1) : '-';
  const fmtDays = (v) => (typeof v === 'number' && Number.isFinite(v)) ? String(v) : '-';

  const thStyle = { padding: '4px 6px', textAlign: 'center', color: '#6c757d', fontSize: '0.6rem', fontWeight: 600, borderBottom: '1px solid #333', whiteSpace: 'nowrap', position: 'sticky', top: 0, backgroundColor: '#1a1d21' };
  const tdCenter = { padding: '4px 6px', textAlign: 'center', color: '#adb5bd', fontSize: '0.7rem', whiteSpace: 'nowrap' };
  const tdName = { padding: '4px 6px', textAlign: 'left', color: '#dee2e6', fontSize: '0.7rem', whiteSpace: 'nowrap' };

  // On mobile, fill the viewport horizontally under the trigger so the
  // popover can't bleed off the right edge; on desktop anchor to the
  // trigger's left edge with a comfortable minWidth.
  const popoverStyle = mobile
    ? {
        position: 'fixed',
        top: pos.top,
        left: '4px',
        right: '4px',
        maxHeight: `calc(100vh - ${pos.top + 8}px)`,
      }
    : {
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        minWidth: '300px',
        maxHeight: '340px',
      };

  const popover = open ? ReactDOM.createPortal(
    <div
      ref={popoverRef}
      style={{
        ...popoverStyle,
        zIndex: 10000,
        backgroundColor: '#1a1d21',
        border: '1px solid #444',
        borderRadius: '4px',
        overflowY: 'auto',
        boxShadow: '0 6px 16px rgba(0,0,0,0.5)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: 'left' }}>Pitcher</th>
            <th style={thStyle}>Hand</th>
            <th style={thStyle} title="Average inning entered (season)">Avg Inn</th>
            <th style={thStyle} title="Days since last appearance">Days</th>
          </tr>
        </thead>
        <tbody>
          {isActive && (
            <tr
              onClick={() => { onSelect(null); setOpen(false); }}
              style={{ cursor: 'pointer', borderBottom: '1px solid #333' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <td colSpan={4} style={{ padding: '5px 6px', textAlign: 'center', color: '#adb5bd', fontStyle: 'italic', fontSize: '0.7rem' }}>
                Reset to probable pitcher
              </td>
            </tr>
          )}
          {bullpenList.map(p => {
            const isSel = String(p.id) === String(selectedId);
            return (
              <tr
                key={p.id}
                onClick={() => { onSelect(p); setOpen(false); }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isSel ? 'rgba(255,193,7,0.18)' : 'transparent',
                  borderBottom: '1px solid rgba(51,51,51,0.5)',
                }}
                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <td style={tdName}>
                  {p.rank ? <span style={{ color: '#ffc107', fontWeight: 700, marginRight: '4px' }}>{p.rank}.</span> : null}
                  {p.name}
                </td>
                <td style={tdCenter}>{p.hand || '-'}</td>
                <td style={tdCenter}>{fmtAvgInn(p.avgEnteredInning)}</td>
                <td style={tdCenter}>{fmtDays(p.daysSinceLastAppearance)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        style={{
          marginLeft: '4px',
          backgroundColor: isActive ? '#ffc107' : '#2a2d31',
          color: isActive ? '#000' : '#fff',
          border: '1px solid #444',
          borderRadius: '4px',
          padding: '1px 6px',
          fontSize: mobile ? '0.7rem' : '0.75rem',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {label} {open ? '▴' : '▾'}
      </button>
      {popover}
    </>
  );
};

export default BullpenDropdown;
